import { useAuth0 } from '@auth0/auth0-react';
import InfoTooltip from 'components/InfoTooltip';
import { ReactComponent as AddNewField } from 'assets/icons/Group 239993.svg';
import { ReactComponent as DeleteCost } from 'assets/icons/PendingIcon.svg';
import { InputField, InputFieldWithCheckbox, LabelField, MyCheckbox, SelectField, HintField } from 'components';
import { getIn } from 'formik';
import Button from 'components/button/Button';
import AddInstallmentsPayment from 'components/Installments/AddInstallmentsPayment';
import PricingTabs from 'components/pricingTabs/PricingTabs';
import RadioButtons from 'components/RadioButtons/RadioButtons';
import { getDateFormat, getDateFormatForDatePicket, maskDate } from 'helper/dateFormat';
import { getCurrency, numberFormat } from 'helper/numberFormat';
import React, { useCallback, useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { Stack } from '@mui/material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toastr } from 'react-redux-toastr';
import MaskedInput from 'react-text-mask';
import createAutoCorrectedDatePipe from 'text-mask-addons/dist/createAutoCorrectedDatePipe';
import { getOrganizationInfoFromLocalStorage } from 'utils/functions';
import FinanceService from '../../../../../Lib/API/Finance';
import { isSameMoment } from '../../multiDateChanges';
import './CostInfo.scss';
import LedgersMenu from './LedgersMenu';
import OptionalTypesItems from './OptionalTypesItems';
import PaymentTypeSelect from './PaymentTypeSelect';
import './Pricing.scss';
import { fontConfig } from "theme/fontConfig";

const PriceInfo = ({ formik }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [ledgers, setLedgers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [costActive, setCostActive] = useState(0);
  const [newPriceValue, setNewPriceValue] = useState('');

  const dateMask = maskDate();
  const placeholderFormat = getDateFormat();
  const dateFormate = getDateFormatForDatePicket();
  const currency = getCurrency();
  const autoCorrectedDatePipe = createAutoCorrectedDatePipe(dateFormate || 'mm/dd/yyyy');

  const orgDetails = getOrganizationInfoFromLocalStorage();
  const enableInstallments = orgDetails?.allowInstallments;


  useEffect(() => {
    async function getLedgeraccounts() {
      setLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const res = await FinanceService.getLedgerSubLedgerList(token);
        if (res?.ledgers) {
          setLedgers(res?.ledgers);
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    }
    getLedgeraccounts();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (!formik.values.multiDateEvent) return;

    const isIndividual = formik.values.registrationType === 'individual';
    const prices = formik.values.prices || [];
    const ticketIndex = prices.findIndex(p => p.type === 'Tickets');
    if (ticketIndex === -1) return;

    const ticketPrice = prices[ticketIndex];
    const currentVariants = ticketPrice.variantInfo || [];
    const multiDateVariants = currentVariants.filter(v => v.isMultiDateVariant);
    // For individual mode only keep explicitly user-added variants (not the default placeholder row)
    const manualVariants = currentVariants.filter(v => !v.isMultiDateVariant && (isIndividual ? v.userAddedVariant === true : true));

    if (!isIndividual) {
      // Group mode: strip any leftover isMultiDateVariant entries (e.g. switched from individual)
      if (multiDateVariants.length > 0) {
        const remaining = manualVariants.length > 0 ? manualVariants : [{
          name: '', quantity: '', infiniteQuantity: false, amount: '', reserveProduct: 0, forMemberOnly: false, openAmount: false,
        }];
        formik.setFieldValue(`prices[${ticketIndex}].variantInfo`, remaining);
      }
      return;
    }

    // Individual mode: sync one variant per date
    const allDatesWithLabels = [
      { date: formik.values.date, label: formik.values.dateLabel || '' },
      ...(formik.values.additionalEventDates || []).map(d => ({ date: d.date, label: d.label || '' })),
    ].filter(d => Boolean(d.date));

    if (allDatesWithLabels.length === 0) return;

    // Guard: skip if already in sync to prevent infinite loop
    const alreadySynced =
      ticketPrice.variant === true &&
      multiDateVariants.length === allDatesWithLabels.length &&
      multiDateVariants.every((v, i) =>
        String(v.eventDate) === String(allDatesWithLabels[i].date) &&
        (v.dateLabel || '') === (allDatesWithLabels[i].label || '')
      );

    if (alreadySynced) return;

    // A saved date can be moved: its variant (name, price, quantity, sold count) moves with it.
    // Match by date first, then a variant whose old date no longer exists, by label or by the same row.
    const currentTimes = allDatesWithLabels.map(d => new Date(d.date).getTime());
    const isMoved = (j) => !currentTimes.includes(new Date(multiDateVariants[j].eventDate).getTime());
    const claimed = new Set();
    const movedDates = new Map(); // old date time -> new date
    const findExisting = (date, label, i) => {
      const free = (j) => !claimed.has(j);
      let j = multiDateVariants.findIndex((v, k) => free(k) && isSameMoment(v.eventDate, date));
      if (j < 0 && label) j = multiDateVariants.findIndex((v, k) => free(k) && isMoved(k) && (v.dateLabel || '') === label);
      if (j < 0 && multiDateVariants[i] && free(i) && isMoved(i)) j = i;
      if (j < 0) return undefined;
      claimed.add(j);
      if (!isSameMoment(multiDateVariants[j].eventDate, date)) {
        movedDates.set(new Date(multiDateVariants[j].eventDate).getTime(), date);
      }
      return multiDateVariants[j];
    };

    const newMultiDateVariants = allDatesWithLabels.map(({ date, label }, i) => {
      const existing = findExisting(date, label, i);
      return {
        name: existing?.name || '',
        quantity: existing?.quantity || '',
        infiniteQuantity: existing?.infiniteQuantity || false,
        amount: existing?.amount || '',
        forMemberOnly: existing?.forMemberOnly || false,
        openAmount: false,
        reserveProduct: existing?.reserveProduct || 0,
        isMultiDateVariant: true,
        eventDate: date,
        dateLabel: label,
      };
    });

    // manually-added variants that pointed at a moved date follow it
    const syncedManualVariants = manualVariants.map(v => {
      const movedTo = v.eventDate && movedDates.get(new Date(v.eventDate).getTime());
      return movedTo ? { ...v, eventDate: movedTo } : v;
    });

    // Multi-date variants first, manually-added variants after
    formik.setFieldValue(`prices[${ticketIndex}].variantInfo`, [...newMultiDateVariants, ...syncedManualVariants]);
    formik.setFieldValue(`prices[${ticketIndex}].variant`, true);
  }, [formik.values.multiDateEvent, formik.values.registrationType, formik.values.date, formik.values.dateLabel, formik.values.additionalEventDates, formik.values.prices]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync requiresEventDate on all manual variants when registration type or multiDate mode changes
  useEffect(() => {
    const isIndividualMultiDate = formik.values.multiDateEvent && formik.values.registrationType === 'individual';
    const prices = formik.values.prices || [];
    prices.forEach((price, i) => {
      if (price.type === 'Addons' || !price.variant || !Array.isArray(price.variantInfo)) return;
      const needsUpdate = price.variantInfo.some(v =>
        !v.isMultiDateVariant && (isIndividualMultiDate ? !v.requiresEventDate : v.requiresEventDate)
      );
      if (needsUpdate) {
        const updated = price.variantInfo.map(v =>
          v.isMultiDateVariant ? v : { ...v, requiresEventDate: isIndividualMultiDate ? true : undefined }
        );
        formik.setFieldValue(`prices[${i}].variantInfo`, updated);
      }
    });
  }, [formik.values.registrationType, formik.values.multiDateEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  const regex = /([0-9]*[\.|\,]{0,1}[0-9]{0,2})/s;


  const acceptNumbersAndGtZero = (value) => {
    const updatedValue = parseInt(value) > 0 ? parseInt(value) : '';
    return updatedValue;
  };


  const handleBeforeAddPrice = async () => {
    const errors = await formik.validateForm();
    // Manually inject variantInfo.amount errors for Tickets/Addons
    if (!formik.values.freeEvent && !formik.values.freeForm) {
      (formik.values.prices || []).forEach((price, i) => {
        if ((price.type === 'Tickets' || price.type === 'Addons') && price.variant && Array.isArray(price.variantInfo)) {
          price.variantInfo.forEach((item, vi) => {
            const amount = item?.amount;
            if (amount === '' || amount === undefined || amount === null) {
              if (!errors.prices) errors.prices = [];
              if (!errors.prices[i]) errors.prices[i] = {};
              if (!errors.prices[i].variantInfo) errors.prices[i].variantInfo = [];
              if (!errors.prices[i].variantInfo[vi]) errors.prices[i].variantInfo[vi] = {};
              errors.prices[i].variantInfo[vi].amount = 'Required';
            }
          });
        }
      });
    }
    const hasPriceErrors =
      Array.isArray(errors?.prices) &&
      errors.prices.some((e) => e && Object.keys(e).length > 0);

    if (hasPriceErrors) {
      const newTouched = { ...formik.touched };
      newTouched.prices = formik.values.prices.map((price, i) => {
        const existing = formik.touched?.prices?.[i] || {};
        if (price.type === 'No Payment Needed') return existing;
        const quantityTouched = !(price.type === 'Addons' && price.limitType === 'checkbox');
        const fields = { ...existing, amount: true, quantity: quantityTouched, minimumOpenAmount: true, ledger: true };
        if (price.isInstallmentTransaction) {
          fields.installmentDetailsTransaction = { numberOfInstallment: true };
        }
        if (price.variant && Array.isArray(price.variantInfo)) {
          fields.variantInfo = price.variantInfo.map((_, vIndex) => ({
            ...(formik.touched?.prices?.[i]?.variantInfo?.[vIndex] || {}),
            name: true, amount: true, quantity: true,
          }));
        }
        return fields;
      });
      formik.setTouched(newTouched);
      formik.setErrors(errors);
      toastr.info('Validation', 'Please fill in all required fields before adding a new price.');
      return false;
    }
    return true;
  };

  const handleAddPrice = (title) => {
    const ticketExists = formik.values?.prices?.some((p) => p.type === 'Tickets');
    formik.setFieldValue('prices', [
      ...formik.values?.prices,
      {
        title: title,
        amount: '',
        type: ticketExists ? 'No Payment Needed' : 'Tickets',
        ledger: {},
        subLedger: {},
        tax_deductible: '',
        tax_type: '$',
        chargeDate: '',
        dueDate: '',
        note: '',
        is_splitted: false,
        variant: false,
        isRequired: false,
        quantity: '',
        minimumOpenAmount: '',
        infiniteQuantity: true,
        limitType: 'amount',
        variantInfo: [
          {
            name: '',
            quantity: '',
            infiniteQuantity: true,
            reserveProduct: 0,
            amount: '',
            forMemberOnly: false,
            openAmount: false,
          }
        ],
      },
    ]);
  };

  const handleDeletePrice = (index) => {
    const prices = formik.values?.prices;
    prices.splice(index, 1);
    formik.setFieldValue('prices', prices);
  };

  const handleEditPrice = (index, newTitle) => {
    formik.setFieldValue(`prices[${index}].title`, newTitle);
  };

  const handleReorderPrices = (sourceIndex, destinationIndex) => {
    const prices = [...formik.values.prices];
    const [movedItem] = prices.splice(sourceIndex, 1);
    prices.splice(destinationIndex, 0, movedItem);
    formik.setFieldValue('prices', prices);
  };

  const checkDuplicatePrice = (title, excludeIndex = -1) => {
    return formik.values?.prices?.some(
      (item, idx) => idx !== excludeIndex && item?.title?.toLowerCase() === title.trim().toLowerCase()
    );
  };

  function convertUTCToLocalDate(date) {
    if (!date) {
      return date;
    }
    date = new Date(date);
    date = new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate()
    );
    return date;
  }

  function convertLocalToUTCDate(date, name) {
    if (!date) {
      return date;
    }
    date = new Date(date);
    date = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    );
    formik.setFieldValue(name, date);
    return date;
  }
  const exceptThisSymbols = ['e', 'E', '+', '-'];
  const preventArrows = (e) => {
    if (e.which === 38 || e.which === 40 || exceptThisSymbols.includes(e.key))
      e.preventDefault();
  };
  const removeVariantHandler = (index, variantIndex) => {
    const variants = formik.values?.prices?.[index]?.variantInfo;
    variants.splice(variantIndex, 1);
    formik.setFieldValue(`prices[${index}].variantInfo`, variants);
  };


  const hasPriceError = (index) => {
    const errors = formik.errors?.prices?.[index];
    const touched = formik.touched?.prices?.[index];
    if (!errors) return false;
    const check = (errObj, touchObj) => {
      return Object.keys(errObj || {}).some((key) => {
        const errVal = errObj[key];
        const touchVal = touchObj?.[key];
        if (typeof errVal === "object" && errVal !== null) {
          return check(errVal, touchVal);
        }
        if (key === 'minimumOpenAmount') return Boolean(errVal);
        return Boolean(errVal) && Boolean(touchVal);
      });
    };
    return check(errors, touched);
  };

  const checkAmountTitle = useCallback(type => {
    return type?.toLowerCase() === "optional" ? "Price" : "Amount";
  }, [])

  const checkDisplayVariant = useCallback((index) => {
    return formik.values?.prices?.[index]?.type === 'Addons' || formik.values?.prices?.[index]?.type === 'Tickets'
  }, [formik.values?.prices])

  const checkVariantLength = (index) => {
    return formik.values?.prices?.[index]?.type === 'Tickets' && formik.values?.prices?.[index]?.variantInfo?.length < 10;
  }

  const isVariantFilled = (variant) => {
    if (!variant?.name?.trim()) return false;
    if (!variant?.amount && !variant?.openAmount) return false;
    if (!variant?.infiniteQuantity && !variant?.quantity) return false;
    return true;
  };

  const canAddVariant = (index) => {
    const variants = formik.values?.prices?.[index]?.variantInfo || [];
    return variants.every(isVariantFilled);
  };

  const canAddVariantForDate = (index, date) => {
    const variants = formik.values?.prices?.[index]?.variantInfo || [];
    const variantsForDate = variants.filter(v => String(v.eventDate) === String(date));
    return variantsForDate.every(v => {
      if (!v.isMultiDateVariant && !v?.name?.trim()) return false;
      if (!v?.amount && !v?.openAmount) return false;
      if (!v?.infiniteQuantity && !v?.quantity) return false;
      return true;
    });
  };

  const resetVariantData = (index) => {
    formik.setFieldValue(`prices[${index}].quantity`, '');
    formik.setFieldValue(`prices[${index}].infiniteQuantity`, true);
    const hasVariant = formik.values?.prices?.[index]?.variant;
    if (hasVariant) {
      const existingVariants = formik.values?.prices?.[index]?.variantInfo || [];
      formik.setFieldValue(
        `prices[${index}].variantInfo`,
        existingVariants.map(v => ({ ...v, quantity: '', infiniteQuantity: true }))
      );
    }
  };

  const componentRender = (index) => {

    const allowInstallments = enableInstallments && formik.values?.prices?.[index]?.type !== 'No Payment Needed';

    return (
      <>
        <Row style={{ marginBottom: '20px' }}>
          <Col className="col-sm-6">
            <PaymentTypeSelect
              titleStyle={{
                fontFamily: fontConfig.GeneralPaymentTypeSelectFont.family,
                fontWeight: '400',
                fontSize: '12px',
                lineHeight: '18px',
                color: '#4F4F4F',
              }}
              title={'Type'}
              formik={formik}
              index={index}
              setCostActive={setCostActive}
              items={
                formik.values?.prices?.some((p, i) => i !== index && p.type === 'Tickets')
                  ? ['No Payment Needed', 'Addons', 'Open Amount']
                  : ['Tickets', 'No Payment Needed', 'Addons', 'Open Amount']
              }
              value={formik.values?.prices?.[index]?.type}
            />
          </Col>
          {(formik.values?.prices?.[index]?.type === 'Addons' && formik.values?.prices?.[index]?.variant) || (formik.values?.prices?.[index]?.type === 'Tickets' && formik.values?.prices?.[index]?.variant) || (formik.values?.prices?.[index]?.type === 'No Payment Needed') ? null : (
            <Col className="col-sm-6">
              {formik.values?.prices?.[index]?.type === 'Open Amount' ? (
                <InputField
                  titleStyle={{
                    fontFamily: fontConfig.GeneralInputFieldFont.family,
                    fontWeight: '400',
                    fontSize: '12px',
                    lineHeight: '18px',
                    color: '#4F4F4F',
                  }}
                  style={{ color: '#002E5C' }}
                  mandatory={true}
                  formik={formik}
                  title="Minimum Amount"
                  name={`prices[${index}].minimumOpenAmount`}
                  placeholder="Minimum Amount"
                  checkValidation={true}
                  value={formik.values?.prices?.[index]?.minimumOpenAmount || ''}
                  handleChange={(e) => {
                    const { value } = e.target;
                    const updatedValue = value.match(regex)[0] >= 1 ? value.match(regex)[0] : '';
                    if (parseFloat(updatedValue) > 1000000) return;
                    formik.setFieldValue(`prices[${index}].minimumOpenAmount`, updatedValue);
                  }}
                  onBlur={formik.handleBlur}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                    }
                  }}
                />
              ) : (
                <InputField
                  titleStyle={{
                    fontFamily: fontConfig.GeneralInputFieldFont.family,
                    fontWeight: '400',
                    fontSize: '12px',
                    lineHeight: '18px',
                    color: '#4F4F4F',
                  }}
                  style={{ color: '#002E5C' }}
                  mandatory={true}
                  formik={formik}
                  title={checkAmountTitle(formik.values?.prices?.[index]?.type)}
                  name={`prices[${index}].amount`}
                  placeholder={checkAmountTitle(formik.values?.prices?.[index]?.type)}
                  checkValidation={true}
                  value={formik.values?.prices?.[index]?.amount || ''}
                  handleChange={(e) => {
                    const { value } = e.target;
                    const minimumValue = formik.values?.prices?.[index]?.type !== 'No Payment Needed' ? 0 : 1;
                    const updatedValue = value.match(regex)[0] >= minimumValue ? value.match(regex)[0] : '';
                    if (parseFloat(updatedValue) > 1000000) return;
                    formik.setFieldValue(`prices[${index}].amount`, updatedValue);
                  }}
                  onBlur={formik.handleBlur}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                    }
                  }}
                />
              )}
            </Col>
          )}
        </Row>
        {formik.values?.prices?.[index]?.type === 'Open Amount' && (
          <Row style={{ marginBottom: '20px' }}>
            <Col className="col-sm-6">
              <InputField
                titleStyle={{
                  fontFamily: fontConfig.GeneralInputFieldFont.family,
                  fontWeight: '400',
                  fontSize: '12px',
                  lineHeight: '18px',
                  color: '#4F4F4F',
                }}
                style={{ color: '#002E5C' }}
                mandatory={true}
                formik={formik}
                title="Quantity"
                name={`prices[${index}].quantity`}
                placeholder="Quantity"
                checkValidation={true}
                value={formik.values?.prices?.[index]?.quantity}
                handleChange={(e) => {
                  const value = acceptNumbersAndGtZero(e?.target?.value.match(regex)[0]);
                  if (value > 10000) return;
                  formik.setFieldValue(`prices[${index}].quantity`, value);
                }}
                onBlur={formik.handleBlur}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
                disabled={formik.values?.prices?.[index]?.infiniteQuantity}
              />
            </Col>
            <Col className="col-sm-6">
              <MyCheckbox
                title="No Limit"
                name="infiniteQuantity"
                handlechange={(e) => {
                  formik.setFieldValue(`prices[${index}].infiniteQuantity`, e.target.checked);
                  if (e.target.checked) {
                    formik.setFieldValue(`prices[${index}].quantity`, '');
                    formik.setFieldTouched(`prices[${index}].quantity`, false);
                  }
                }}
                checked={formik.values?.prices?.[index]?.infiniteQuantity}
                style={{ marginTop: '40px' }}
              />
            </Col>
          </Row>
        )}
        {checkDisplayVariant(index) && (
          <Row style={{ marginBottom: '20px', alignItems: 'center' }}>
            <Col className="col-sm-6">
              <Stack
                direction={{ xs: "column", xl: "row" }}
                spacing={{ xs: 0, xl: 2 }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'nowrap' }}>
                  <MyCheckbox
                    title="Has Variant"
                    name="variant"
                    disabled={formik.values.multiDateEvent && formik.values.registrationType === 'individual' && formik.values?.prices?.[index]?.type === 'Tickets'}
                    handlechange={(e) => {
                      formik.setFieldValue(
                        `prices[${index}].variant`,
                        e.target.checked
                      );
                      if (e.target.checked) {
                        formik.setFieldValue(`prices[${index}].quantity`, '');
                        formik.setFieldValue(`prices[${index}].infiniteQuantity`, false);
                        if (!formik.values?.prices?.[index]?.variantInfo?.length) {
                          formik.setFieldValue(`prices[${index}].variantInfo`, [{
                            name: '',
                            quantity: '',
                            infiniteQuantity: true,
                            amount: '',
                            reserveProduct: 0,
                            forMemberOnly: false,
                            openAmount: false,
                          }]);
                        }
                      } else {
                        formik.setFieldValue(`prices[${index}].infiniteQuantity`, true);
                      }
                    }}
                    checked={formik.values?.prices?.[index]?.variant}
                  />
                  <InfoTooltip text={
                    formik.values?.prices?.[index]?.type === 'Addons'
                      ? "Create multiple add-on options with different prices. E.g., 'Lunch - $15', 'Dinner - $30', 'T-Shirt - $20'. Each variant can have its own quantity and price."
                      : "Create multiple ticket tiers under one ticket type. E.g., 'Standard - $50', 'VIP - $150', 'Student - $25'. Each variant has its own price and availability."
                  } style={{ marginBottom: '8px' }} />
                </div>
                {formik.values?.prices?.[index]?.type === 'Addons' && (
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'nowrap' }}>
                    <MyCheckbox
                      title="Required"
                      name="isRequired"
                      handlechange={(e) => {
                        formik.setFieldValue(`prices[${index}].isRequired`, e.target.checked);
                        if (e.target.checked) {
                          formik.setFieldValue(`prices[${index}].quantity`, '');
                          formik.setFieldValue(`prices[${index}].infiniteQuantity`, true);
                          const variantInfo = formik.values?.prices?.[index]?.variantInfo;
                          if (Array.isArray(variantInfo)) {
                            formik.setFieldValue(
                              `prices[${index}].variantInfo`,
                              variantInfo.map(v => ({ ...v, quantity: '', infiniteQuantity: true }))
                            );
                          }
                        }
                      }}
                      checked={formik.values?.prices?.[index]?.isRequired}
                    />
                    <InfoTooltip text="Make this option mandatory for all registrants." style={{ marginBottom: '8px' }} />
                  </div>
                )}
              </Stack>
            </Col>
            {formik.values?.prices?.[index]?.type === 'Addons' && (
              <Col className="col-sm-6">
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <RadioButtons
                    value={formik.values?.prices?.[index]?.limitType}
                    name={"limitType"}
                    onChange={(value) => {
                      formik.setFieldValue(
                        `prices[${index}].limitType`,
                        value
                      );
                      if (value === 'checkbox') {
                        resetVariantData(index);
                        formik.setFieldTouched(`prices[${index}].quantity`, false);
                      }
                    }}
                    size={"small"}
                    row={true}
                    options={[
                      {
                        label: "Amount",
                        value: "amount"
                      },
                      {
                        label: "Checkbox",
                        value: "checkbox"
                      }
                    ]}
                  />
                  <InfoTooltip text="Use a checkbox for simple yes/no add-ons, and an amount field when users need to select multiple units or quantities." style={{ marginBottom: '5px' }} />
                </div>
              </Col>
            )}
          </Row>
        )}
        {formik.values?.prices?.[index]?.type === 'Addons' && !formik.values?.prices?.[index]?.variant && (
          <Row style={{ marginBottom: '20px' }}>
            <Col className="col-sm-6">
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <label style={{ fontFamily: fontConfig.EventsCostInfoTextFont.family, fontWeight: '500', fontSize: '12px', color: '#4F4F4F', marginBottom: 0 }}>Quantity <span style={{ color: 'red' }}>*</span></label>
                <InfoTooltip text="Limit how many times this add-on can be selected across all registrations. E.g., Only 30 parking passes available, or 50 meal tickets. Leave blank or check No Limit for unlimited." />
              </div>
              <InputField
                titleStyle={{
                  fontFamily: fontConfig.GeneralInputFieldFont.family,
                  fontWeight: '400',
                  fontSize: '12px',
                  lineHeight: '18px',
                  color: '#4F4F4F',
                }}
                style={{ color: '#002E5C' }}
                mandatory={true}
                formik={formik}
                title="none"
                name={`prices[${index}].quantity`}
                placeholder="Quantity"
                checkValidation={true}
                value={
                  formik.values?.prices?.[index]?.quantity
                }
                handleChange={(e) => {
                  const value = acceptNumbersAndGtZero(e?.target?.value.match(regex)[0]);
                  if (value > 10000) return;
                  formik.setFieldValue(
                    `prices[${index}].quantity`,
                    value
                  )
                }
                }
                onBlur={formik.handleBlur}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
                disabled={formik.values?.prices?.[index]?.infiniteQuantity || formik.values?.prices?.[index]?.limitType === 'checkbox'}
              />
            </Col>
            <Col className="col-sm-6">
              <MyCheckbox
                title="No Limit"
                name="infiniteQuantity"
                handlechange={(e) => {
                  formik.setFieldValue(
                    `prices[${index}].infiniteQuantity`,
                    e.target.checked
                  ).then(() => formik.validateForm());
                  if (e.target.checked) {
                    formik.setFieldValue(`prices[${index}].quantity`, '');
                    formik.setFieldTouched(`prices[${index}].quantity`, false);
                  }
                }}
                checked={formik.values?.prices?.[index]?.infiniteQuantity}
                disabled={formik.values?.prices?.[index]?.limitType === 'checkbox' || formik.values?.prices?.[index]?.isRequired === true}
                style={{ marginTop: '40px' }}
              />
            </Col>
          </Row>
        )}
        {checkDisplayVariant(index) && formik.values?.prices?.[index]?.variant && (() => {
          const isIndividualMultiDateTicket =
            formik.values?.multiDateEvent &&
            formik.values?.registrationType === 'individual' &&
            formik.values?.prices?.[index]?.type === 'Tickets';
          const variantInfo = formik.values?.prices?.[index]?.variantInfo || [];

          if (isIndividualMultiDateTicket) {
            const indexedVariants = variantInfo.map((v, i) => ({ ...v, _idx: i }));
            const multiDateVars = indexedVariants.filter(v => v.isMultiDateVariant);
            const getLabelForDate = (date) => {
              if (String(formik.values.date) === String(date)) return formik.values.dateLabel || '';
              const match = (formik.values.additionalEventDates || []).find(d => String(d.date) === String(date));
              return match?.label || '';
            };

            return (
              <>
                {multiDateVars.map((dateVar, dateVarPos) => {
                  const date = dateVar.eventDate;
                  const manualForDate = indexedVariants.filter(
                    v => !v.isMultiDateVariant && String(v.eventDate) === String(date)
                  );
                  return (
                    <React.Fragment key={dateVarPos}>
                      {dateVarPos > 0 && (
                        <Row style={{ marginBottom: '20px' }}>
                          <Col className="col-sm-12">
                            <hr className="w-100 mb-5" style={{ borderBottom: 'dashed 1px #002E5C', opacity: '0.2' }} />
                          </Col>
                        </Row>
                      )}
                      <OptionalTypesItems
                        formik={formik}
                        index={index}
                        variantIndex={dateVar._idx}
                        removeVariantHandler={removeVariantHandler}
                        acceptNumbersAndGtZero={acceptNumbersAndGtZero}
                        showVariantName={manualForDate.length > 0}
                      />
                      {manualForDate.map((manualVar) => (
                        <React.Fragment key={manualVar._idx}>
                          <Row style={{ marginBottom: '20px' }}>
                            <Col className="col-sm-12">
                              <hr className="w-100 mb-5" style={{ borderBottom: 'dashed 1px #002E5C', opacity: '0.2' }} />
                            </Col>
                          </Row>
                          <OptionalTypesItems
                            formik={formik}
                            index={index}
                            variantIndex={manualVar._idx}
                            removeVariantHandler={removeVariantHandler}
                            acceptNumbersAndGtZero={acceptNumbersAndGtZero}
                          />
                        </React.Fragment>
                      ))}
                      <Row style={{ marginBottom: '20px' }}>
                        <Col className="col-sm-12">
                          <button
                            className="optional_type_add_btn"
                            onClick={() => {
                              if (!canAddVariantForDate(index, date)) {
                                toastr.info('Validation', 'Please fill in all variant details before adding a new one');
                                return;
                              }
                              formik.setFieldValue(`prices[${index}].variantInfo`, [
                                ...variantInfo,
                                {
                                  name: '',
                                  quantity: '',
                                  infiniteQuantity: true,
                                  amount: '',
                                  requiresEventDate: true,
                                  userAddedVariant: true,
                                  eventDate: date,
                                  dateLabel: dateVar.dateLabel || getLabelForDate(date),
                                },
                              ]);
                            }}
                          >
                            <AddNewField />
                            Add Price
                          </button>
                        </Col>
                      </Row>
                    </React.Fragment>
                  );
                })}
              </>
            );
          }

          return (
            <>
              {variantInfo.map((item, variantIndex) => (
                <React.Fragment key={variantIndex}>
                  {formik.values?.prices?.[index]?.type === 'Tickets' && variantIndex > 0 && (
                    <Row style={{ marginBottom: '20px' }}>
                      <Col className="col-sm-12">
                        <hr className="w-100 mb-5" style={{ borderBottom: 'dashed 1px #002E5C', opacity: '0.2' }} />
                      </Col>
                    </Row>
                  )}
                  <OptionalTypesItems
                    formik={formik}
                    index={index}
                    variantIndex={variantIndex}
                    removeVariantHandler={removeVariantHandler}
                    acceptNumbersAndGtZero={acceptNumbersAndGtZero}
                  />
                </React.Fragment>
              ))}
              {(checkVariantLength(index) || formik.values?.prices?.[index]?.type === 'Addons') && (
                <Row style={{ marginBottom: '20px' }}>
                  <Col className="col-sm-12">
                    <button className="optional_type_add_btn" onClick={() => {
                      if (!canAddVariant(index)) {
                        toastr.info('Validation', 'Please fill in all variant details before adding a new one');
                        return;
                      }
                      formik.setFieldValue(`prices[${index}].variantInfo`, [...variantInfo, {
                        name: '',
                        quantity: '',
                        infiniteQuantity: true,
                        amount: '',
                      }]);
                    }}>
                      <AddNewField />
                      Add
                    </button>
                  </Col>
                </Row>
              )}
            </>
          );
        })()}
        {(formik.values?.prices?.[index]?.type === 'Addons' || formik.values?.prices?.[index]?.type === 'Open Amount' || formik.values?.prices?.[index]?.type === 'Tickets') && (
          <Row style={{ marginBottom: '20px' }}>
            <Col className="col-sm-12">
              <hr
                className="w-100 mb-5"
                style={{ borderBottom: 'dashed 1px #002E5C', opacity: '0.2' }}
              />
            </Col>
          </Row>
        )}

        {formik.values?.prices?.[index]?.type !== 'No Payment Needed' && (
          <Row style={{ marginBottom: '20px' }}>
            <Col className="col-sm-6">
              <LabelField
                mandatory
                title="Ledgers"
                titleStyle={{
                  fontFamily: fontConfig.GeneralLabelFieldFont.family,
                  fontWeight: '400',
                  fontSize: '12px',
                  lineHeight: '18px',
                  color: '#4F4F4F',
                  margin: '0px',
                }}
              />
              <LedgersMenu
                ledgers={ledgers}
                formik={formik}
                index={index}
                loading={loading}
                onOpen={() => formik.setFieldTouched(`prices[${index}].ledger`, true)}
              />
              {getIn(formik.touched, `prices[${index}].ledger`) &&
                getIn(formik.errors, `prices[${index}].ledger`) && (
                  <HintField error={getIn(formik.errors, `prices[${index}].ledger`)} />
                )}
            </Col>
            <Col className="col-sm-6">
              <div className="class_tax_deductible">
                <InputField
                  titleStyle={{
                    fontFamily: fontConfig.GeneralInputFieldFont.family,
                    fontWeight: '400',
                    fontSize: '12px',
                    lineHeight: '18px',
                    color: '#4F4F4F',
                  }}
                  style={{ background: '#f6f6f6', color: '#002E5C' }}
                  formik={formik}
                  title="Tax Deductible"
                  name="tax_deductible"
                  placeholder="Tax Deductible"
                  checkValidation={true}
                  value={formik.values?.prices?.[index]?.tax_deductible ?? ''}
                  handleChange={(e) => {
                    const val = e.target.value.match(regex)[0];
                    const maxVal = formik.values?.prices?.[index]?.tax_type === '%' ? 100 : 10000;
                    if (parseFloat(val) > maxVal) return;
                    formik.setFieldValue(`prices[${index}.tax_deductible]`, val);
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                    }
                  }}
                />
                <div className="tax_type">
                  <span
                    onClick={() => {
                      formik.setFieldValue(`prices[${index}].tax_type`, '$');
                      formik.setFieldValue(`prices[${index}].tax_deductible`, '');
                    }}
                    className={
                      formik.values?.prices?.[index]?.tax_type === '$'
                        ? 'text-primary tax_type_selected'
                        : ''
                    }
                  >
                    {currency}
                  </span>
                  <span>|</span>
                  <span
                    onClick={() => {
                      formik.setFieldValue(`prices[${index}].tax_type`, '%');
                      formik.setFieldValue(`prices[${index}].tax_deductible`, '');
                    }}
                    className={
                      formik.values?.prices?.[index]?.tax_type === '%'
                        ? 'text-primary tax_type_selected'
                        : ''
                    }
                  >
                    %
                  </span>
                </div>
              </div>
            </Col>
          </Row>
        )}
        {formik.values?.prices?.[index]?.type === 'On Approve' && (
          <Row style={{ marginBottom: '20px' }}>
            <Col className="col-sm-6">
              <LabelField title={'Charge Date'} mandatory />
              <DatePicker
                isClearable
                showIcon
                placeholderText={placeholderFormat}
                dateFormat={dateFormate}
                selected={
                  formik.values?.prices?.[index]?.chargeDate &&
                  // new Date(formik?.values?.date_of_death)
                  convertUTCToLocalDate(
                    formik.values?.prices?.[index]?.chargeDate
                  )
                }
                onChange={(e) => {
                  formik.setFieldValue(`prices[${index}.chargeDate]`, e);
                  convertLocalToUTCDate(e, `prices[${index}.chargeDate]`);
                }}
                customInput={
                  <MaskedInput
                    pipe={autoCorrectedDatePipe}
                    mask={dateMask}
                    keepCharPositions={true}
                    guide={true}
                  />
                }
              />
            </Col>
            <Col className="col-sm-6">
              <LabelField title={'Due Date'} mandatory />
              <DatePicker
                isClearable
                showIcon
                placeholderText={placeholderFormat}
                dateFormat={dateFormate}
                selected={
                  formik.values?.prices?.[index]?.dueDate &&
                  // new Date(formik?.values?.date_of_death)
                  convertUTCToLocalDate(formik.values?.prices?.[index]?.dueDate)
                }
                onChange={(e) => {
                  formik.setFieldValue(`prices[${index}].dueDate`, e);
                  convertLocalToUTCDate(e, `prices[${index}].dueDate`);
                }}
                customInput={
                  <MaskedInput
                    pipe={autoCorrectedDatePipe}
                    mask={dateMask}
                    keepCharPositions={true}
                    guide={true}
                  />
                }
              />
            </Col>
          </Row>
        )}
        <Row style={{ marginBottom: '20px' }}>
          <Col className="col-sm-12">
            <InputField
              titleStyle={{
                fontFamily: fontConfig.GeneralInputFieldFont.family,
                fontWeight: '400',
                fontSize: '12px',
                lineHeight: '18px',
                color: '#4F4F4F',
              }}
              style={{ background: '#f6f6f6', color: '#002E5C' }}
              formik={formik}
              mandatory={formik.values?.prices?.[index]?.required_notes ?? false}
              title="Note"
              name="note"
              placeholder="Note"
              checkValidation={true}
              maxLength={200}
              value={formik.values?.prices?.[index]?.note ?? ''}
              handleChange={(e) =>
                formik.setFieldValue(`prices[${index}].note`, e.target.value)
              }
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
            />
          </Col>
        </Row>
        <hr
          className="w-100 mb-5"
          style={{ borderBottom: 'dashed 1px #002E5C', opacity: '0.2' }}
        />
        <div className="d-flex" style={{ gap: '20px' }}>
          {formik.values?.prices?.[index]?.type === 'On Approve' && (
            <MyCheckbox
              title="Split Charge"
              name="split_charge"
              handlechange={(e) => {
                formik.setFieldValue(
                  `prices[${index}].is_splitted`,
                  e.target.checked
                );
                formik.setFieldValue(
                  `prices[${index}].isInstallmentTransaction`,
                  false
                );
              }}
              checked={formik.values?.prices?.[index]?.is_splitted}
              disabled={formik.values?.prices?.[index]?.type !== 'On Approve'}
            />
          )}
          {allowInstallments && (
            <MyCheckbox
              title="Allow Installments"
              name="isInstallmentTransaction"
              handlechange={(e) => {
                formik.setFieldValue(
                  `prices[${index}].isInstallmentTransaction`,
                  e.target.checked
                );
                formik.setFieldValue(
                  `prices[${index}].is_splitted`,
                  false
                );
              }}
              checked={formik.values?.prices?.[index]?.isInstallmentTransaction || false}
            />
          )}
        </div>
        {(formik.values.prices[index]?.isInstallmentTransaction && allowInstallments) && (
          <AddInstallmentsPayment
            formik={formik}
            formikKey={`prices[${index}].installmentDetailsTransaction`}
            onInstallmentChange={(value) => {
              if (!value) {
                formik.setFieldValue(`prices.${index}.installmentDetailsTransaction.numberOfInstallment`, null);
                return;
              }
              formik.setFieldValue(`prices.${index}.installmentDetailsTransaction.numberOfInstallment`, value?.value);
            }}
            onDateChange={(date) => {
              formik.setFieldValue(`prices.${index}.installmentDetailsTransaction.lastInstallmentDate`, date);
              convertLocalToUTCDate(date, `prices.${index}.installmentDetailsTransaction.lastInstallmentDate`);
            }}
            installmentValue={formik.values.prices[index]?.installmentDetailsTransaction?.numberOfInstallment}
            dateValue={
              formik.values.prices[index]?.installmentDetailsTransaction?.lastInstallmentDate &&
              convertUTCToLocalDate(formik.values.prices[index]?.installmentDetailsTransaction?.lastInstallmentDate)
            }
            manageFromOutside={true}
          />
        )}
        {formik.values?.prices?.[index]?.is_splitted && (
          <Row>
            <div className="col-sm-5">
              <SelectField
                {...{
                  formik,
                  name: `values.${index}.split_duration`,
                  items: ['day', 'week', 'month', 'quarter', 'year'],
                  placeholder: 'Select',
                  value: formik.values?.prices?.[index]?.split_duration ? (
                    <span style={{ color: 'black' }}>
                      {formik.values?.prices?.[index]?.split_duration}
                    </span>
                  ) : (
                    'Select'
                  ),
                }}
                onClick={(data) =>
                  formik.setFieldValue(`prices[${index}].split_duration`, data)
                }
              />
            </div>
            {formik.values?.prices?.[index]?.split_duration ? (
              <div className="col-sm-3">
                <InputField
                  type="number"
                  {...{
                    formik,
                    title: '',
                    name: `values.${index}.number_of_split`,
                    placeholder: '0',
                    value: formik.values?.prices?.[index]?.number_of_split,
                    checkValidation: true,
                  }}
                  onKeyDown={(e) => preventArrows(e)}
                  handleChange={(e) =>
                    formik.setFieldValue(
                      `prices[${index}].number_of_split`,
                      e.target.value
                    )
                  }
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                    }
                  }}
                />
                {/* <div className="placeholder-div">
                  <p>{formik.values.values?.[index]?.split_amount}</p>
                </div> */}
              </div>
            ) : (
              ''
            )}
          </Row>
        )}
      </>
    );
  };
  const total = formik.values?.prices?.reduce(
    (acc, item) => acc + Number(item.amount || 0),
    0
  );
  return (
    <div className="cost_info_container">
      <div className="cost_info_left_side">
        <div className="cost_info_title">
          <PricingTabs
            items={formik.values?.prices || []}
            activeTab={costActive}
            setActiveTab={setCostActive}
            onAdd={handleAddPrice}
            onDelete={handleDeletePrice}
            onEdit={handleEditPrice}
            onReorder={handleReorderPrices}
            duplicateCheck={checkDuplicatePrice}
            titleKey="title"
            minItems={0}
            hasError={hasPriceError}
            onBeforeAdd={handleBeforeAddPrice}
          />
        </div>
        <div class="vertical-line"></div>
      </div>
      <div className="cost_info_content">
        {formik.values?.prices?.length > 0 ? (
          componentRender(costActive)
        ) : (
          <div className="no_cost text-center">No Cost Found</div>
        )}
      </div>
    </div>
  );
};

export default PriceInfo;
